/**
 * PWA API client - direct HTTP calls to the Cloudflare Worker.
 * Unlike the desktop app (which uses IPC), the PWA calls the REST API directly.
 *
 * Includes automatic JWT refresh: when a 401 is received, the client attempts
 * to refresh the token and retries the request once. If refresh fails, the
 * session is cleared and a RefreshFailedError is thrown so stores can redirect
 * to login.
 */

import type {
  Event, Task, Note,
  CreateEventInput, UpdateEventInput,
  CreateTaskInput, UpdateTaskInput,
  CreateNoteInput, UpdateNoteInput,
} from '@anna-calendar/shared';

// Default API base for development; production uses same origin
const DEFAULT_API_BASE = 'https://anna-calendar-api.awesometools.workers.dev';

export function getApiBase(): string {
  return import.meta.env.VITE_API_BASE || DEFAULT_API_BASE;
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (res.status === 401) {
      throw new ApiError('Unauthorized', 401);
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || `Request failed: ${res.status}`, res.status);
  }
  return res.json();
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown when JWT refresh fails and the user must re-authenticate. */
export class RefreshFailedError extends Error {
  constructor() {
    super('Session expired. Please log in again.');
    this.name = 'RefreshFailedError';
  }
}

// ─── JWT Refresh Handler ──────────────────────────────────────────────────
// Module-level refresh callback registered by the auth store. Avoids circular
// imports between api/client.ts and store/auth.ts.

type RefreshHandler = () => Promise<string | null>;

let refreshHandler: RefreshHandler | null = null;

/**
 * Register a token refresh handler. Called once by the auth store on init.
 * The handler should attempt to refresh the JWT and return the new token,
 * or return null if refresh failed.
 */
export function setRefreshHandler(handler: RefreshHandler): void {
  refreshHandler = handler;
}

/**
 * Execute an API call with automatic JWT retry on 401.
 *
 * If the initial request returns 401, attempts to refresh the token via the
 * registered handler. If refresh succeeds, retries the request once with the
 * new token. If refresh fails, throws RefreshFailedError.
 *
 * @param getToken - returns the current JWT token
 * @param requestFn - function that performs the fetch using the given token
 */
export async function fetchWithAuthRetry<T>(
  getToken: () => string | null,
  requestFn: (token: string) => Promise<T>,
): Promise<T> {
  const token = getToken();
  if (!token) throw new RefreshFailedError();

  try {
    return await requestFn(token);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    // 401 received - attempt token refresh
  }

  if (!refreshHandler) throw new RefreshFailedError();

  const newToken = await refreshHandler();
  if (!newToken) throw new RefreshFailedError();

  // Retry with the refreshed token
  return requestFn(newToken);
}

// ─── Auth ──────────────────────────────────────────────────────────────────

export async function login(passphrase: string): Promise<{ token: string; refreshToken: string }> {
  const res = await fetch(`${getApiBase()}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ passphrase }),
  });
  return handleResponse(res);
}

export async function refreshToken(refreshTokenValue: string): Promise<{ token: string; refreshToken: string }> {
  const res = await fetch(`${getApiBase()}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
  });
  return handleResponse(res);
}

// ─── Paginated response type ───────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// ─── Events ────────────────────────────────────────────────────────────────

export async function fetchEvents(
  token: string,
  params?: { start?: string; end?: string; limit?: number; offset?: number },
): Promise<Event[]> {
  const qs = new URLSearchParams();
  if (params?.start) qs.set('start', params.start);
  if (params?.end) qs.set('end', params.end);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs}` : '';
  const res = await fetch(`${getApiBase()}/api/events${query}`, { headers: authHeaders(token) });

  if (params?.limit !== undefined) {
    // Paginated response: unwrap the data array
    const paginated = await handleResponse<PaginatedResponse<Event>>(res);
    return paginated.data;
  }
  return handleResponse(res);
}

export async function createEvent(token: string, data: CreateEventInput): Promise<Event> {
  const res = await fetch(`${getApiBase()}/api/events`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateEvent(token: string, id: string, data: UpdateEventInput): Promise<Event> {
  const res = await fetch(`${getApiBase()}/api/events/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteEvent(token: string, id: string): Promise<void> {
  const res = await fetch(`${getApiBase()}/api/events/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || `Delete failed: ${res.status}`, res.status);
  }
}

// ─── Tasks ─────────────────────────────────────────────────────────────────

export async function fetchTasks(
  token: string,
  params?: {
    due_date?: string;
    due_date_start?: string;
    due_date_end?: string;
    priority?: number;
    limit?: number;
    offset?: number;
  },
): Promise<Task[]> {
  const qs = new URLSearchParams();
  if (params?.due_date) qs.set('due_date', params.due_date);
  if (params?.due_date_start) qs.set('due_date_start', params.due_date_start);
  if (params?.due_date_end) qs.set('due_date_end', params.due_date_end);
  if (params?.priority !== undefined) qs.set('priority', String(params.priority));
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs}` : '';
  const res = await fetch(`${getApiBase()}/api/tasks${query}`, { headers: authHeaders(token) });

  if (params?.limit !== undefined) {
    const paginated = await handleResponse<PaginatedResponse<Task>>(res);
    return paginated.data;
  }
  return handleResponse(res);
}

export async function createTask(token: string, data: CreateTaskInput): Promise<Task> {
  const res = await fetch(`${getApiBase()}/api/tasks`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateTask(token: string, id: string, data: UpdateTaskInput): Promise<Task> {
  const res = await fetch(`${getApiBase()}/api/tasks/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteTask(token: string, id: string): Promise<void> {
  const res = await fetch(`${getApiBase()}/api/tasks/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || `Delete failed: ${res.status}`, res.status);
  }
}

// ─── Notes ─────────────────────────────────────────────────────────────────

export async function fetchNotes(
  token: string,
  params?: { date?: string; limit?: number; offset?: number },
): Promise<Note[]> {
  const qs = new URLSearchParams();
  if (params?.date) qs.set('date', params.date);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs}` : '';
  const res = await fetch(`${getApiBase()}/api/notes${query}`, { headers: authHeaders(token) });

  if (params?.limit !== undefined) {
    const paginated = await handleResponse<PaginatedResponse<Note>>(res);
    return paginated.data;
  }
  return handleResponse(res);
}

export async function createNote(token: string, data: CreateNoteInput): Promise<Note> {
  const res = await fetch(`${getApiBase()}/api/notes`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateNote(token: string, id: string, data: UpdateNoteInput): Promise<Note> {
  const res = await fetch(`${getApiBase()}/api/notes/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteNote(token: string, id: string): Promise<void> {
  const res = await fetch(`${getApiBase()}/api/notes/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || `Delete failed: ${res.status}`, res.status);
  }
}
